import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const REVIEW_ERROR_MESSAGES = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  platform_admin_required: '플랫폼 관리자만 유니온 등록을 심사할 수 있습니다.',
  platform_admin_scope_mismatch: '담당하는 커뮤니티 유형(갤러리/채널)의 유니온만 심사할 수 있습니다.',
  union_not_found: '심사할 유니온을 찾을 수 없습니다.',
  union_not_pending: '이미 심사가 끝난 유니온입니다.',
  union_creator_missing: '유니온 신청자 정보를 확인할 수 없습니다.',
  pending_leader_membership_missing: '대표 승인 대기 정보를 확인할 수 없습니다.',
  rejection_reason_too_long: '거절 사유는 500자 이하로 입력해주세요.',
};

function requireSupabaseClient() {
  if (!hasSupabaseConfig || !supabase) {
    throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  }

  return supabase;
}

function createReviewError(error, fallbackMessage) {
  const rawMessage = String(error?.message || '');
  const matchedCode = Object.keys(REVIEW_ERROR_MESSAGES).find((code) => rawMessage.includes(code));
  const reviewError = new Error(
    REVIEW_ERROR_MESSAGES[matchedCode] || fallbackMessage || '유니온 등록 심사를 처리하지 못했습니다.',
  );
  reviewError.code = matchedCode || error?.code || 'unknown_error';
  return reviewError;
}

function pendingRegistrationRpcMissing(error) {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return code === '42883'
    || code === 'PGRST202'
    || message.includes('list_pending_union_registrations');
}

function mapPendingRegistration(row) {
  const applicantUserId = String(row?.applicant_user_id || row?.created_by || '').trim();
  const applicantOpenid = String(
    row?.applicant_normalized_openid || row?.applicant_openid || '',
  ).trim();

  return {
    id: row?.union_id,
    name: row?.union_name,
    guild_id: row?.union_guild_id,
    registration_status: row?.registration_status,
    community_type: String(row?.community_type || ''),
    created_by: row?.created_by,
    requested_at: row?.requested_at,
    leaderMembership: row?.membership_id ? {
      id: row.membership_id,
      union_id: row.union_id,
      user_id: applicantUserId,
      role: 'leader',
      status: 'pending',
      requested_at: row.requested_at,
    } : null,
    applicantProfile: applicantUserId || row?.applicant_nickname || applicantOpenid ? {
      user_id: applicantUserId,
      openid: String(row?.applicant_openid || '').trim(),
      normalized_openid: String(row?.applicant_normalized_openid || '').trim(),
      nickname: String(row?.applicant_nickname || '').trim(),
      guild_id: String(row?.applicant_guild_id || '').trim(),
      guild_name: String(row?.applicant_guild_name || '').trim(),
    } : null,
  };
}

async function loadPendingUnionRegistrationsLegacy(client) {
  const { data: unions, error: unionsError } = await client
    .from('unions')
    .select('id,name,guild_id,registration_status,created_by,requested_at')
    .eq('registration_status', 'pending')
    .order('requested_at', { ascending: true });

  if (unionsError) {
    throw createReviewError(unionsError, '승인 대기 유니온을 불러오지 못했습니다.');
  }

  if (!unions?.length) return [];

  const unionIds = unions.map((union) => union.id);
  const { data: leaderMemberships, error: membershipsError } = await client
    .from('union_memberships')
    .select('id,union_id,user_id,role,status,requested_at')
    .in('union_id', unionIds)
    .eq('role', 'leader')
    .eq('status', 'pending');

  if (membershipsError) {
    throw createReviewError(membershipsError, '대표 승인 대기 정보를 불러오지 못했습니다.');
  }

  const leaderUserIds = Array.from(new Set((leaderMemberships || []).map((membership) => membership.user_id)));
  let gameProfiles = [];

  if (leaderUserIds.length) {
    const { data, error } = await client
      .from('game_profiles')
      .select('user_id,openid,normalized_openid,nickname,guild_id,guild_name')
      .in('user_id', leaderUserIds);

    if (error) {
      throw createReviewError(error, '신청자 게임 프로필을 불러오지 못했습니다.');
    }

    gameProfiles = data || [];
  }

  const membershipByUnionId = new Map(
    (leaderMemberships || []).map((membership) => [membership.union_id, membership]),
  );
  const profileByUserId = new Map(gameProfiles.map((profile) => [profile.user_id, profile]));

  return unions.map((union) => {
    const membership = membershipByUnionId.get(union.id) || null;
    const applicantProfile = membership ? profileByUserId.get(membership.user_id) || null : null;

    return {
      ...union,
      leaderMembership: membership,
      applicantProfile,
    };
  });
}

export async function loadPendingUnionRegistrations() {
  const client = requireSupabaseClient();
  const {
    data: { user },
    error: userError,
  } = await client.auth.getUser();

  if (userError || !user?.id) {
    throw createReviewError({ message: 'authentication_required' });
  }

  const { data: adminRow, error: adminError } = await client
    .from('platform_admins')
    .select('user_id, scope')
    .eq('user_id', user.id)
    .maybeSingle();

  if (adminError) {
    throw createReviewError(adminError, '플랫폼 관리자 상태를 확인하지 못했습니다.');
  }

  if (!adminRow) {
    return { isPlatformAdmin: false, isFullAdmin: false, applications: [] };
  }

  const isFullAdmin = adminRow.scope === null;
  const { data, error } = await client.rpc('list_pending_union_registrations');

  if (!error) {
    return {
      isPlatformAdmin: true,
      isFullAdmin,
      applications: (data || []).map(mapPendingRegistration),
    };
  }

  if (!pendingRegistrationRpcMissing(error)) {
    throw createReviewError(error, '승인 대기 유니온과 신청자 정보를 불러오지 못했습니다.');
  }

  return {
    isPlatformAdmin: true,
    isFullAdmin,
    applications: await loadPendingUnionRegistrationsLegacy(client),
  };
}

export async function reviewUnionRegistration({ unionId, approve, rejectionReason = '' }) {
  const client = requireSupabaseClient();
  const normalizedReason = String(rejectionReason || '').trim();
  const { data, error } = await client.rpc('review_union_registration', {
    target_union_id: unionId,
    approve: Boolean(approve),
    rejection_reason: approve ? null : normalizedReason || null,
  });

  if (error) {
    throw createReviewError(error, approve ? '유니온 승인을 완료하지 못했습니다.' : '유니온 거절을 완료하지 못했습니다.');
  }

  if (data !== (approve ? 'active' : 'rejected')) {
    throw createReviewError(null, '유니온 심사 결과를 확인하지 못했습니다.');
  }

  return data;
}
